/**
 * Реестр открытых модалок.
 *
 * Нужен, чтобы кнопка «Назад» в Telegram закрывала верхнюю панель, а не уводила
 * со страницы. `useTelegramNavigation` живёт в App и про модалки ничего не знает,
 * а модалки рендерятся глубоко в дереве — общий контекст пришлось бы протаскивать
 * через все страницы. Модуль вместо этого держит стек снаружи React.
 *
 * Брендбук 7.4.
 */

// Стек колбэков закрытия: последний открытый — последний в массиве
const stack = [];
const listeners = new Set();

const notify = () => {
  listeners.forEach((listener) => listener(stack.length));
};

/**
 * Регистрирует открытую панель. Возвращает функцию снятия с учёта —
 * её удобно вернуть прямо из `useEffect`.
 */
export const pushModal = (onClose) => {
  const entry = { onClose };
  stack.push(entry);
  notify();

  return () => {
    const index = stack.indexOf(entry);
    if (index !== -1) {
      stack.splice(index, 1);
      notify();
    }
  };
};

/**
 * Закрывает верхнюю панель. Возвращает true, если было что закрывать, —
 * по нему `useTelegramNavigation` решает, навигировать или нет.
 *
 * Саму запись из стека не снимаем: её снимет эффект модалки, когда она
 * отработает закрытие. Иначе при отменённом закрытии стек разъедется.
 */
export const closeTopModal = () => {
  const top = stack[stack.length - 1];
  if (top) {
    top.onClose();
    return true;
  }

  // Окон нет — может перехватить экран со своим состоянием «вглубь»
  const handler = backHandlers[backHandlers.length - 1];
  return handler ? handler() !== false : false;
};

// ── Перехватчики «Назад» без окна ─────────────────────────────────────────────
// Экран бывает «углублён» без модалки: развёрнутая панель прогулки, просмотр
// другого места. «Назад» там должен вернуть на шаг, а не увести со страницы.
// В общий стек это не кладём: он считает окна и прячет нижнее меню
const backHandlers = [];

/**
 * Регистрирует перехватчик «Назад». Срабатывает, только если открытых окон
 * нет. Возвращает функцию снятия — удобно вернуть из `useEffect`.
 * Перехватчик возвращает false, если обрабатывать нечего.
 */
export const pushBackHandler = (handler) => {
  const entry = () => handler();
  backHandlers.push(entry);
  return () => {
    const index = backHandlers.indexOf(entry);
    if (index !== -1) backHandlers.splice(index, 1);
  };
};

export const hasOpenModal = () => stack.length > 0;

// ── Блокировка скролла фона ───────────────────────────────────────────────────
// Со счётчиком, а не флагом: панель может открыться поверх другой. Если каждая
// сама запоминает и возвращает body.style.overflow, закрытие нижней раньше
// верхней вернёт скролл фону под ещё открытой панелью.
let lockCount = 0;
let overflowBeforeLock = '';

/** Блокирует скролл body. Возвращает функцию снятия блокировки. */
export const lockBodyScroll = () => {
  if (lockCount === 0) {
    overflowBeforeLock = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  lockCount += 1;

  let released = false;
  return () => {
    // Защита от повторного вызова: React может дёрнуть очистку эффекта дважды
    if (released) return;
    released = true;

    lockCount -= 1;
    if (lockCount === 0) {
      document.body.style.overflow = overflowBeforeLock;
    }
  };
};

/** Подписка на изменение глубины стека. Возвращает функцию отписки. */
export const subscribeModalStack = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Для `useSyncExternalStore`: getSnapshot возвращает примитив, поэтому
 * лишних перерисовок не будет — React сравнивает результат по значению.
 */
export const getModalCount = () => stack.length;
